import { ContentCopyOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Container,
  LinearProgress,
  linearProgressClasses,
  Link,
  Skeleton,
  Stack,
  styled,
  Typography,
  useTheme
} from "@mui/material";
import {
  ArrowLeft20Px,
  formatDateForTimeZone,
  formatTimeInTimeZone,
  mainColours,
  NewTab20Px,
  useToast,
  isPastTime,
  Checkmark20Px
} from "@qubit/autoparts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";
import envConstants from "~/config/envConstants";
import { setSearchText } from "~/features/batches/adminBatches.slice";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import {
  useGetOrdersQuery,
  usePostUnstallOrderMutation
} from "~/redux/warehouse/orders.hooks";
import { useGetPicksQuery } from "~/redux/warehouse/pick.hooks";
import { useGetTotesQuery } from "~/redux/warehouse/totes.hooks";

import { ToteCard } from "./ToteCard";

type OrderStatus =
  | "received"
  | "batched"
  | "in progress"
  | "picked"
  | "stalled";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200]
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.statusBlue.main
  }
}));

export function OrderSidePanel({
  orderId,
  onClose
}: {
  orderId: Guid;
  onClose: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const backNavLabel = pathname.includes("/admin-batches")
    ? t("nav.viewname.batches")
    : t("orders");
  const usersFC = useAppSelector(selectUsersFulfillmentCenter);
  const navigate = useFcNavigate();
  const { errorToast, successToast } = useToast();
  const dispatch = useAppDispatch();

  const areDevCheatsEnabled =
    envConstants.DEV_CHEATS_UAT === "true" ||
    envConstants.DEV_CHEATS_STAGING === "true";

  const {
    data: orders,
    isFetching: isFetchingOrders,
    error: orderError
  } = useGetOrdersQuery(
    {
      orderId
    },
    { refetchOnMountOrArgChange: true }
  );

  const {
    data: totes,
    isFetching: isFetchingTotes,
    error: toteError
  } = useGetTotesQuery(
    {
      orderId
    },
    { refetchOnMountOrArgChange: true }
  );

  const {
    data: picks,
    isFetching: isFetchingPicks,
    error: pickError
  } = useGetPicksQuery(
    {
      orderId
    },
    { refetchOnMountOrArgChange: true }
  );

  const [postUnstallOrder] = usePostUnstallOrderMutation();

  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  // eslint-disable-next-line react-you-might-not-need-an-effect/no-reset-all-state-on-prop-change
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
    setCopiedToClipboard(false);
  }, [orderId]);

  useEffect(() => {
    if (orderError || toteError || pickError) {
      errorToast(getMessageFromRtkError(orderError || toteError || pickError));
    }
  }, [errorToast, orderError, toteError, pickError]);

  const order = orders?.[0];

  const isOrderStalled = order?.status.toLowerCase() === "stalled";

  const handleUnstallOrder = async () => {
    if (!order?.orderId || !isOrderStalled) {
      return;
    }
    try {
      await postUnstallOrder({ orderIds: [order.orderId] }).unwrap();
      successToast(t("unstall order success title"), {
        description: t("unstall order success description")
      });
    } catch (err: unknown) {
      errorToast(getMessageFromRtkError(err));
    }
  };

  const batchNames = [...new Set(totes?.flatMap((item) => item.batchNames))];

  const orderStates: OrderStatus[] = [
    "received",
    "batched",
    "stalled",
    "in progress",
    "picked"
  ];
  // only show stalled state if the order is stalled
  const displayOrderStates =
    order?.status.toLowerCase() !== "stalled"
      ? orderStates.filter((state) => state !== "stalled")
      : orderStates;

  const isOrderInProgress = totes?.some(
    (tote) =>
      tote.status.toLowerCase() === "picking" &&
      order?.status.toLowerCase() !== "stalled"
  );
  const orderStatus = isOrderInProgress
    ? "in progress"
    : (order?.status.toLowerCase() as OrderStatus);

  const showOrderProgress = orderStates.includes(orderStatus);

  const orderProgress =
    (displayOrderStates.indexOf(orderStatus) /
      (displayOrderStates.length - 1)) *
    100;

  const isPastDue =
    order?.pickingEndTime &&
    isPastTime(order.pickingEndTime, usersFC?.timeZone);
  const pickingEndTimeColor = isPastDue ? "error" : "inherit";

  const infoRow = (label: string, value: React.ReactNode) => {
    return (
      <Stack direction="row" gap={2} marginBottom={1}>
        <Typography variant="body2" width="200px">
          {label}:
        </Typography>
        {typeof value === "string" ? (
          <Typography variant="body2">{value}</Typography>
        ) : (
          value
        )}
      </Stack>
    );
  };

  if (isFetchingOrders || isFetchingTotes || isFetchingPicks || !order) {
    return (
      <Container>
        <Stack gap={2} padding={2}>
          <Skeleton
            variant="rectangular"
            aria-label="loading order"
            height={50}
          />
          <Skeleton variant="rectangular" height={100} />
          <Skeleton variant="rectangular" height={100} />
          <Skeleton variant="rectangular" height={600} />
        </Stack>
      </Container>
    );
  }

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        onClick={onClose}
        py={1}
        px={2}
        sx={{
          cursor: "pointer"
        }}
      >
        <ArrowLeft20Px style={{ fill: theme.palette.text.secondary }} />
        <Typography variant="subtitle1" color="textSecondary">
          {backNavLabel}
        </Typography>
      </Stack>
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="h6" fontSize="1.5rem">
            {t("order")} #: {order?.externalOrderId}
          </Typography>
          {isOrderStalled && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleUnstallOrder}
              sx={{ ml: "auto" }}
            >
              {t("unstall")}
            </Button>
          )}
        </Stack>
        {showOrderProgress && (
          <Stack direction="column" gap={0.5} m={2}>
            <Box mx={2}>
              <BorderLinearProgress
                value={Math.max(orderProgress, 5)}
                variant="determinate"
              />
            </Box>
            <Stack direction="row" justifyContent="space-between">
              {displayOrderStates.map((state) => (
                <Typography
                  key={state}
                  variant="body2"
                  fontWeight={state === orderStatus ? "bold" : "normal"}
                  color={
                    state === orderStatus ? "textPrimary" : "textSecondary"
                  }
                >
                  {t(state)}
                </Typography>
              ))}
            </Stack>
          </Stack>
        )}
        <Typography variant="subtitle1" color="textSecondary" marginTop={2}>
          {t("order summary")}
        </Typography>
        <Box
          border={`1px solid ${theme.palette.divider}`}
          borderRadius={1}
          padding={3}
          marginTop={1}
        >
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" gap={1} alignItems="center" marginBottom={2}>
              <Typography variant="subtitle2">{order?.customerName}</Typography>
              {(isOrderStalled || !showOrderProgress) && (
                <Chip
                  label={t(orderStatus)}
                  aria-label={order?.status.toLowerCase()}
                  sx={{ backgroundColor: mainColours.label[6] }}
                />
              )}
            </Stack>
            <Typography variant="subtitle2">
              {totes?.length || 0} {t("totes")}
            </Typography>
          </Stack>

          {infoRow(
            t("batch names"),
            <Stack direction="column" gap={1}>
              {batchNames?.map((batchName) => (
                <Stack
                  direction="row"
                  gap={1}
                  key={batchName}
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    const dateParam =
                      !!order &&
                      formatDateForTimeZone(
                        order.earliestPickingStartTime ?? order.createdDate,
                        usersFC?.timeZone
                      );
                    const params = new URLSearchParams();
                    if (dateParam) params.set("date", dateParam);
                    dispatch(setSearchText(batchName));
                    navigate(`/admin-batches?${params.toString()}`);
                  }}
                  alignItems="center"
                >
                  <Link
                    variant="body2"
                    color="primary"
                    underline="hover"
                    key={batchName}
                  >
                    {batchName}
                  </Link>
                  <NewTab20Px fill={theme.palette.primary.main} />
                </Stack>
              ))}
            </Stack>
          )}
          {infoRow(
            t("created date"),
            formatDateForTimeZone(order.createdDate, usersFC?.timeZone)
          )}
          {infoRow(
            t("due date"),
            formatDateForTimeZone(order.pickingEndTime, usersFC?.timeZone)
          )}
          {infoRow(
            t("earliest picking start time"),
            formatTimeInTimeZone(
              order.earliestPickingStartTime,
              usersFC?.timeZone
            )
          )}
          {infoRow(
            t("order picking end time"),
            <Typography variant="body2" color={pickingEndTimeColor}>
              {order.pickingEndTime
                ? formatTimeInTimeZone(order.pickingEndTime, usersFC?.timeZone)
                : " - "}
            </Typography>
          )}
          {infoRow(t("order priority"), order.priority)}
          {areDevCheatsEnabled &&
            infoRow(
              "Order Id",
              <Stack direction="column" gap={0}>
                <Stack
                  direction="row"
                  gap={1}
                  key={order.orderId}
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    if (navigator.clipboard?.writeText) {
                      navigator.clipboard
                        .writeText(order.orderId)
                        .then(() => setCopiedToClipboard(true))
                        .catch(() => {});
                    }
                  }}
                  alignItems="center"
                >
                  <Typography
                    variant="body2"
                    key={order.orderId}
                    aria-label="order id"
                  >
                    {order.orderId}
                  </Typography>
                  <ContentCopyOutlined
                    fontSize="small"
                    sx={{ fill: theme.palette.text.secondary }}
                  />
                </Stack>
                {copiedToClipboard ? (
                  <Stack
                    direction="row"
                    gap={0.5}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Checkmark20Px
                      fontSize="small"
                      style={{
                        fill: theme.palette.success.main,
                        width: "14px"
                      }}
                    />
                    <Typography variant="body2" color="success.main">
                      Copied to clipboard
                    </Typography>
                  </Stack>
                ) : (
                  <Box height="20px" />
                )}
              </Stack>
            )}
        </Box>
      </Container>
      <Container sx={{ marginBottom: 2 }}>
        <Typography
          variant="subtitle1"
          color="textSecondary"
          marginTop={3}
          marginBottom={1}
        >
          {t("tote details")}
        </Typography>
        {totes
          ?.toSorted((a, b) => a.toteNumber - b.toteNumber)
          .map((tote) => {
            const totePicks =
              picks?.filter((pick) => pick.assignedToteId === tote.toteId) ??
              [];
            return (
              <ToteCard
                key={tote.toteId}
                tote={tote}
                picks={totePicks}
                totalTotes={totes.length}
              />
            );
          })}
      </Container>
    </>
  );
}
